import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Download,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Users as UsersIcon,
  UserCheck,
  Crown,
  UserPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useClients,
  useClientStats,
  useCreateClient,
  useDeleteClient,
  useImportClientsCsv,
} from '@/hooks/useClients';
import { clientsApi } from '@/api/clients.api';
import type { Client, ClientStatus } from '@/types/client.types';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/clientes')({
  component: ClientesPage,
});

const STATUS_VALUES = ['active', 'inactive', 'suspended', 'banned'] as const;

const createClientSchema = z.object({
  nombre: z.string().min(1, 'Requerido').max(120),
  apellido: z.string().max(120).optional().or(z.literal('')),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  telefono: z.string().max(40).optional().or(z.literal('')),
  casino_username: z.string().min(1, 'Requerido').max(120),
  cuit: z.string().max(40).optional().or(z.literal('')),
  saldo: z.coerce.number().min(0).default(0),
  wager: z.coerce.number().min(0).default(0),
  saldo_cobrable: z.coerce.number().min(0).default(0),
  estatus: z.enum(STATUS_VALUES).default('active'),
});

type CreateClientForm = z.infer<typeof createClientSchema>;

const statusBadge: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' }> = {
  active: { label: 'Activo', variant: 'success' },
  inactive: { label: 'Inactivo', variant: 'secondary' },
  suspended: { label: 'Suspendido', variant: 'warning' },
  banned: { label: 'Baneado', variant: 'destructive' },
  vip: { label: 'VIP', variant: 'default' },
  prospect: { label: 'Prospect', variant: 'secondary' },
};

function ClientesPage() {
  const [search, setSearch] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<Client | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: stats } = useClientStats();
  const { data: list, isLoading } = useClients({ search });
  const createMutation = useCreateClient();
  const deleteMutation = useDeleteClient();
  const importMutation = useImportClientsCsv();

  const rows: Client[] = list?.data ?? [];

  const columns = useMemo<ColumnDef<Client>[]>(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.id.slice(0, 8)}</span>,
    },
    {
      accessorKey: 'nombre',
      header: 'Nombre',
      cell: ({ row }) => (
        <span>
          {row.original.nombre} {row.original.apellido ?? ''}
        </span>
      ),
    },
    { accessorKey: 'casino_username', header: 'Usuario Casino' },
    { accessorKey: 'cuit', header: 'CUIT', cell: ({ row }) => row.original.cuit ?? '—' },
    { accessorKey: 'telefono', header: 'Teléfono', cell: ({ row }) => row.original.telefono ?? '—' },
    {
      accessorKey: 'saldo',
      header: 'Saldo',
      cell: ({ row }) => formatCurrency(row.original.saldo),
    },
    {
      accessorKey: 'wager',
      header: 'Wager',
      cell: ({ row }) => formatCurrency(row.original.wager),
    },
    {
      accessorKey: 'estatus',
      header: 'Estado',
      cell: ({ row }) => {
        const cfg = statusBadge[row.original.estatus] ?? { label: row.original.estatus, variant: 'secondary' as const };
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      accessorKey: 'referred_by_name',
      header: 'Referido por',
      cell: ({ row }) => row.original.referred_by_name ?? '—',
    },
    {
      id: 'devices',
      header: 'Dispositivos',
      cell: ({ row }) => row.original.device_info?.ua?.slice(0, 24) ?? '—',
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled>
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDeleteCandidate(row.original)} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], []);

  const handleExport = async () => {
    try {
      const blob = await clientsApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar el CSV');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    importMutation.mutate(file);
    e.target.value = '';
  };

  return (
    <>
      <TopBanner
        title="Clientes"
        subtitle="Jugadores del tenant"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Clientes" value={stats?.total ?? 0} icon={UsersIcon} />
          <StatCard label="Activos" value={stats?.active ?? 0} icon={UserCheck} />
          <StatCard label="VIP" value={stats?.vip ?? 0} icon={Crown} />
          <StatCard label="Referidos" value={stats?.referred ?? 0} icon={UserPlus} />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {list ? `${list.total} cliente(s) en total` : 'Cargando…'}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Descargar CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Importar CSV
            </Button>
            <Button size="sm" onClick={() => setOpenCreate(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <DataTable columns={columns} data={rows} isLoading={isLoading} emptyMessage="No hay clientes todavía" />
        </div>
      </div>

      <NewClientDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onSubmit={async (values) => {
          await createMutation.mutateAsync(values);
          setOpenCreate(false);
        }}
        submitting={createMutation.isPending}
      />

      <Dialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar cliente</DialogTitle>
            <DialogDescription>
              ¿Confirmás eliminar a <strong>{deleteCandidate?.nombre} {deleteCandidate?.apellido}</strong>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCandidate(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteCandidate) {
                  deleteMutation.mutate(deleteCandidate.id, {
                    onSuccess: () => setDeleteCandidate(null),
                  });
                }
              }}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold">{value.toLocaleString('es-AR')}</div>
        </div>
        <Icon className="h-8 w-8 text-muted-foreground/60" />
      </CardContent>
    </Card>
  );
}

interface NewClientDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSubmit: (values: CreateClientForm) => Promise<void> | void;
  submitting: boolean;
}

function NewClientDialog({ open, onOpenChange, onSubmit, submitting }: NewClientDialogProps) {
  const form = useForm<CreateClientForm>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      casino_username: '',
      cuit: '',
      saldo: 0,
      wager: 0,
      saldo_cobrable: 0,
      estatus: 'active' as ClientStatus & (typeof STATUS_VALUES)[number],
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
    form.reset();
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nuevo Cliente</DialogTitle>
          <DialogDescription>Datos básicos del jugador. Los campos con * son obligatorios.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="nombre"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="apellido"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apellido</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="telefono"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="casino_username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario Casino *</FormLabel>
                    <FormControl>
                      <Input placeholder="juan123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cuit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CUIT</FormLabel>
                    <FormControl>
                      <Input placeholder="20-12345678-9" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="saldo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saldo</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="wager"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wager</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="saldo_cobrable"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saldo Cobrable</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="estatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estatus</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="inactive">Inactivo</SelectItem>
                        <SelectItem value="suspended">Suspendido</SelectItem>
                        <SelectItem value="banned">Baneado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Crear
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
